import Search from "../Search/Search"
import { useCallback, useEffect, useState } from "react"
import _debounce from "lodash/debounce"
import ArticlesFilter from "./ArticlesFilter/ArticlesFilter"
import ArticlesFavorites from "./ArticlesFavorites/ArticlesFavorites"
import ArticlesPrevBlock from "./ArticlesPrevBlock/ArticlesPrevBlock"
import ArticlesSearch from "./ArticlesSearch/ArticlesSearch"
import { useGetArticlesTagsQuery, useGetPopularArticlesQuery, useLazyGetArticlesQuery } from "../../redux/api/articles"
import clsx from "clsx"
import { useDispatch } from "react-redux"
import { selectArticles, setFavoriteArticles } from "../../redux/slice/articles"
import { IArticle } from "../../types/content"
import useWindowSize from "../../hooks/useWindowSize"
import ArticlesAside from "./ArticlesAside/ArticlesAside"
import TitleBack from "../TitleBack/TitleBack"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "../../hooks"
import styles from "./Articles.module.scss"

const Articles = ({ isFavoriteLoading }: { isFavoriteLoading?: boolean }) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const dispatch = useDispatch()
  const { isDesktop } = useWindowSize()
  const { favoriteArticles } = useAppSelector(selectArticles)

  const { data: tags, isLoading: isTagsLoading } = useGetArticlesTagsQuery()
  const { data: popularArticles, isLoading: isLoadingPopular } = useGetPopularArticlesQuery()
  const [getSearchArticles] = useLazyGetArticlesQuery()

  const [searchVal, setSearchVal] = useState<string>("")
  const [searchArticles, setSearchArticles] = useState<IArticle[]>([])
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [filter, setFilter] = useState<{ search: string; tags: string[] }>({
    search: "",
    tags: [],
  })

  const debounceFn = useCallback(
    _debounce((str: string) => {
      setFilter((prev) => {
        return { ...prev, search: str }
      })
    }, 500),
    [],
  )

  useEffect(() => {
    debounceFn(searchVal)
  }, [searchVal])

  useEffect(() => {
    if (!filter.tags?.length && !filter.search) return
    setSearchLoading(true)
    getSearchArticles({ search: filter.search, tags: filter.tags }).then(({ data }) => {
      setSearchArticles(data || [])
      setSearchLoading(false)
    })
  }, [filter])

  useEffect(() => {
    dispatch(setFavoriteArticles(favoriteArticles || []))
  }, [favoriteArticles])

  return (
    <div>
      <header className={styles.header}>
        <TitleBack title={t("knowledgeBase")} className={styles.titleBack} />
        <Search searchVal={searchVal} setSearchVal={setSearchVal} className={styles.search} withClear />
      </header>

      {isTagsLoading ? (
        <div className={clsx(styles.tagsLoader)}>
          {[...Array(4)].map((_item, index) => (
            <div key={index} className={clsx("skeletonBlock", styles.tagsLoaderBtn)} />
          ))}
        </div>
      ) : tags?.aTags && tags.aTags.length > 0 ? (
        <ArticlesFilter list={tags.aTags} filter={filter} setFilter={setFilter} className={styles.filter} />
      ) : null}

      {!isDesktop && <ArticlesAside />}

      {filter.search || filter.tags.length > 0 ? (
        <>
          {isSearchLoading ? (
            <div className={clsx("skeletonBlock", styles.searchLoading)} />
          ) : (
            <ArticlesSearch
              isOnlyTag={!filter.search && filter.tags.length > 0}
              clearSearch={() => {
                setSearchVal("")
                setFilter({
                  search: "",
                  tags: [],
                })
              }}
              list={searchArticles}
            />
          )}
        </>
      ) : (
        <>
          {isFavoriteLoading ? <div className={clsx("skeletonBlock", styles.favLoading)} /> : <ArticlesFavorites />}

          {isLoadingPopular ? (
            <div className={styles.bottomGrid}>
              <div className={clsx("skeletonBlock", styles.prevLoading)} />
              <div className={clsx("skeletonBlock", styles.prevLoading)} />
            </div>
          ) : (popularArticles?.new && popularArticles.new.length > 0) ||
            (popularArticles?.popular && popularArticles.popular.length > 0) ? (
            <div className={styles.bottomGrid}>
              {popularArticles?.new && <ArticlesPrevBlock title={t("newToYou")} list={popularArticles.new} />}
              {popularArticles?.popular && (
                <ArticlesPrevBlock title={t("popularArticles")} list={popularArticles.popular} />
              )}
            </div>
          ) : (
            <div className={styles.nothing}>{t("noArticlesYet")}</div>
          )}
        </>
      )}
    </div>
  )
}

export default Articles
